import { useState } from 'react'
import { Mail, UserPlus } from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'
import { Navigate } from 'react-router-dom'
import { supabase } from '@/shared/lib/supabase'
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin'

interface InviteResponse {
  success?: boolean
  invited?: boolean
  reason?: string
  error?: string
}

export function InvitePage() {
  const { isSuperAdmin, loading } = useIsSuperAdmin()
  const [email, setEmail] = useState('')
  const [isSending, setIsSending] = useState(false)

  if (loading) {
    return <p className="text-legal-600 dark:text-legal-400">Cargando...</p>
  }

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const target = email.trim()
    if (!target || !supabase) return
    setIsSending(true)
    try {
      const { data, error } = await supabase.functions.invoke<InviteResponse>('hub-invite-user', {
        body: { email: target, appId: 'signwiz' },
      })
      if (error || !data?.success) {
        toast.error(data?.error || error?.message || 'No se pudo enviar la invitación')
      } else if (data.invited) {
        toast.success(`Invitación enviada a ${target}`)
        setEmail('')
      } else {
        toast(data.reason || 'El usuario ya tiene cuenta, no se envió invitación')
      }
    } catch {
      toast.error('Error inesperado. Por favor, intenta nuevamente.')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="max-w-xl mx-auto">
      <Toaster position="top-right" />
      <div className="bg-white dark:bg-legal-800 rounded-2xl shadow p-6 sm:p-8">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <UserPlus size={24} /> Invitar usuario
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Enviá una invitación por email para que la persona pueda crear su cuenta en SignWiz.
        </p>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="email"
                id="invite-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 dark:border-legal-600 bg-white dark:bg-legal-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-legal-500 focus:border-transparent transition"
                placeholder="persona@email.com"
                required
                disabled={isSending}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={isSending || !email.trim()}
            className="w-full btn-primary py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSending ? 'Enviando...' : 'Enviar invitación'}
          </button>
        </form>
      </div>
    </div>
  )
}
